/**
 * Platform Detection Utilities
 *
 * Detects whether the app is running in a native context (iOS/Android via Capacitor)
 * or in a web browser, and provides platform-specific information.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if running in a native Capacitor app (iOS or Android)
 */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Get the current platform
 * @returns 'ios' | 'android' | 'web'
 */
export function getPlatform(): 'ios' | 'android' | 'web' {
  return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
}

/**
 * Check if running on iOS (native or PWA)
 */
export function isIOS(): boolean {
  return getPlatform() === 'ios';
}

/**
 * Check if running on Android (native or PWA)
 */
export function isAndroid(): boolean {
  return getPlatform() === 'android';
}

/**
 * Check if running in a web browser
 */
export function isWeb(): boolean {
  return getPlatform() === 'web';
}

/**
 * Check if Web MIDI API is available
 * Web MIDI API is NOT available on iOS/WebKit (incl. the Capacitor WKWebView). Native iOS MIDI is provided by the @yomidi/capacitor-coremidi plugin via MidiService, not Web MIDI.
 */
export function isMidiSupported(): boolean {
  return typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
}

/**
 * Get platform-specific MIDI instructions
 */
export function getMidiInstructions(): string {
  const platform = getPlatform();

  if (platform === 'ios') {
    return 'Connect MIDI devices via Bluetooth, USB (with adapter), or network MIDI. iOS MIDI runs through the native CoreMIDI bridge, not Web MIDI.';
  }

  if (platform === 'android') {
    return 'Connect MIDI devices via USB OTG or Bluetooth. Web MIDI API support varies by browser and Android version.';
  }

  // Web browser
  if (isMidiSupported()) {
    return 'Connect MIDI devices via USB or use virtual MIDI ports. Works best in Chrome, Edge, or Opera.';
  }

  return 'Web MIDI is not supported in this browser. Please use Chrome, Edge, or Opera on desktop.';
}

/**
 * Get platform-specific error message for MIDI access
 */
export function getMidiErrorMessage(error?: Error): string {
  const platform = getPlatform();

  if (platform === 'ios') {
    return error
      ? `MIDI access error: ${error.message}. Check iOS Settings > Privacy > Local Network and Bluetooth permissions.`
      : 'MIDI access denied. Please enable Bluetooth and Local Network permissions in iOS Settings.';
  }

  if (platform === 'android') {
    return error
      ? `MIDI access error: ${error.message}. Check Android Settings > Apps > Permissions.`
      : 'MIDI access denied. Please enable necessary permissions in Android Settings.';
  }

  // Web browser
  return error
    ? `MIDI access denied: ${error.message}. Please enable MIDI permissions in your browser settings.`
    : 'MIDI access denied. Please enable MIDI permissions in your browser settings.';
}

/**
 * Check if we should show browser-specific UI (browser icons, download links, etc.)
 */
export function shouldShowBrowserUI(): boolean {
  return isWeb() && !isMidiSupported();
}

/**
 * Check if running in iOS Simulator (development only)
 * Returns true only on iOS simulator, false on physical devices and other platforms
 */
export function isIOSSimulator(): boolean {
  if (!isIOS()) return false;

  // Check user agent for simulator indicators
  const userAgent = navigator.userAgent.toLowerCase();

  // iOS Simulator often has 'simulator' in the platform or user agent
  // Also check for x86_64 architecture (simulators run on Intel/Apple Silicon Macs)
  const hasSimulatorIndicator = userAgent.includes('simulator') ||
                                 userAgent.includes('x86_64') ||
                                 userAgent.includes('intel mac os x');

  // Alternative: Check screen dimensions that are typical for simulators
  // Simulators often have perfect pixel ratios (unused but kept for reference)
  // const _isSimulatorScreenSize = window.devicePixelRatio === 2 || window.devicePixelRatio === 3;

  return hasSimulatorIndicator;
}

/**
 * Check if app should bypass authentication (development only)
 * Returns true for iOS simulator in DEV mode ONLY, false for production builds
 *
 * SECURITY: This function MUST return false in production to prevent auth bypass
 */
export function shouldBypassAuth(): boolean {
  // Only bypass auth on iOS simulator AND in development mode
  // import.meta.env.DEV is false in production builds
  // Convert to boolean explicitly to ensure proper type in tests
  return Boolean(import.meta.env.DEV) && isIOSSimulator();
}
